import type { CSSProperties } from 'react';
import { resolveHudPanelStyle, resolveTeamAccentColor } from './hudDesign';

type ScoreboardPlayer = {
  id: string;
  displayName: string;
  team: string | null;
  score: number;
  kills?: number;
  deaths?: number;
  captures?: number;
  returns?: number;
  grabs?: number;
};

type ScoreboardEvent = {
  category?: string | null;
  combat?: { attackerId?: string } | null;
  flagEvent?: { kind?: string; playerId?: string; carrierId?: string; attackerId?: string } | null;
  message?: string;
};

type ScoreboardProps = {
  visible: boolean;
  players: ScoreboardPlayer[];
  teamScores: Record<string, number>;
  localPlayerId?: string;
  events?: ScoreboardEvent[];
};

function normalizeTeam(team: string | null) {
  const normalized = team?.toLowerCase();
  return normalized === 'red' || normalized === 'blue' ? normalized : null;
}

function teamTone(team: string | null) {
  return resolveTeamAccentColor(team);
}

const panelStyle: CSSProperties = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'min(580px, 90vw)',
  maxHeight: '70vh',
  overflow: 'auto',
  padding: 16,
  ...resolveHudPanelStyle(),
  background: 'rgba(4, 8, 20, 0.88)',
  border: '1px solid rgba(80, 180, 255, 0.22)',
  borderRadius: 8,
  boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
  color: '#edf7ff',
  zIndex: 100,
};

const headerCellStyle: CSSProperties = {
  fontSize: 9.5,
  letterSpacing: 1.4,
  textTransform: 'uppercase',
  color: 'rgba(231,244,252,0.6)',
  padding: '4px 8px',
  textAlign: 'left',
  borderBottom: '1px solid rgba(255,255,255,0.08)',
};

const cellStyle: CSSProperties = {
  fontSize: 11,
  padding: '5px 8px',
  borderBottom: '1px solid rgba(255,255,255,0.04)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

function TeamSection({
  team,
  players,
  teamScore,
  localPlayerId,
  grabsByPlayer,
}: {
  team: string;
  players: ScoreboardPlayer[];
  teamScore: number;
  localPlayerId?: string;
  grabsByPlayer: Record<string, number>;
}) {
  const accent = teamTone(team);
  const sorted = [...players].sort((a, b) => b.score - a.score);

  return (
    <div style={{ marginBottom: 12 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 8px',
          marginBottom: 4,
          background: `${accent}12`,
          borderLeft: `3px solid ${accent}`,
        }}
      >
        <span style={{ fontSize: 11, letterSpacing: 1.8, textTransform: 'uppercase', color: accent, fontWeight: 700 }}>
          {team}
        </span>
        <span style={{ fontSize: 22, fontVariantNumeric: 'tabular-nums', color: '#f4fbff' }}>{teamScore}</span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={headerCellStyle}>Player</th>
            <th style={{ ...headerCellStyle, textAlign: 'right' }}>Score</th>
            <th style={{ ...headerCellStyle, textAlign: 'right' }}>K</th>
            <th style={{ ...headerCellStyle, textAlign: 'right' }}>D</th>
            <th style={{ ...headerCellStyle, textAlign: 'right' }}>Cap</th>
            <th style={{ ...headerCellStyle, textAlign: 'right' }}>Grab</th>
            <th style={{ ...headerCellStyle, textAlign: 'right' }}>Ret</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((player) => (
            <tr
              key={player.id}
              style={{
                background: player.id === localPlayerId ? `${accent}15` : 'transparent',
              }}
            >
              <td style={{ ...cellStyle, color: player.id === localPlayerId ? accent : '#edf7ff', fontWeight: player.id === localPlayerId ? 700 : 400 }}>
                {player.displayName}
              </td>
              <td style={{ ...cellStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{player.score}</td>
              <td style={{ ...cellStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{player.kills ?? 0}</td>
              <td style={{ ...cellStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{player.deaths ?? 0}</td>
              <td style={{ ...cellStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{player.captures ?? 0}</td>
              <td style={{ ...cellStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{player.grabs ?? grabsByPlayer[player.id] ?? 0}</td>
              <td style={{ ...cellStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{player.returns ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Scoreboard({ visible, players, teamScores, localPlayerId, events }: ScoreboardProps) {
  if (!visible) return null;

  const normalizedPlayers = players.map((player) => ({
    ...player,
    team: normalizeTeam(player.team),
  }));
  const redPlayers = normalizedPlayers.filter((p) => p.team === 'red');
  const bluePlayers = normalizedPlayers.filter((p) => p.team === 'blue');

  // Phase 114 — Infer grabs per player from flag-grab events when not tracked in store.
  const grabsByPlayer: Record<string, number> = {};
  (events ?? []).forEach((evt) => {
    const kind = (evt.flagEvent?.kind ?? '').toLowerCase();
    const cat = (evt.category ?? '').toLowerCase();
    const isGrab =
      kind === 'grab' || kind === 'ctf-grab' || kind === 'pickup' || kind === 'steal' ||
      cat === 'grab' || /\bgrab(bed|s)?\b/i.test(evt.message ?? '');
    if (!isGrab) return;
    const pid = evt.flagEvent?.playerId ?? evt.flagEvent?.carrierId ?? evt.flagEvent?.attackerId ?? evt.combat?.attackerId;
    if (pid) grabsByPlayer[pid] = (grabsByPlayer[pid] ?? 0) + 1;
  });

  return (
    <div style={panelStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 10, letterSpacing: 2.2, textTransform: 'uppercase', color: 'rgba(231,244,252,0.55)' }}>
          Scoreboard
        </span>
      </div>
      <TeamSection team="red" players={redPlayers} teamScore={teamScores.red ?? 0} localPlayerId={localPlayerId} grabsByPlayer={grabsByPlayer} />
      <TeamSection team="blue" players={bluePlayers} teamScore={teamScores.blue ?? 0} localPlayerId={localPlayerId} grabsByPlayer={grabsByPlayer} />
      <div style={{ marginTop: 8, textAlign: 'center', fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: 'rgba(231,244,252,0.35)' }}>
        Press Tab to close
      </div>
    </div>
  );
}
